import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Eye, EyeOff } from "lucide-react";
import ConfirmDialog from "@/components/ConfirmDialog";

const EMPTY_USER = { email:"", password:"", name:"", role:"bookstore_user", store_id:"", phone:"" };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // user being edited, or null for create
  const [form, setForm] = useState(EMPTY_USER);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [showPwd, setShowPwd] = useState(false);

  const reload = async () => {
    try {
      const [u, s] = await Promise.all([api.get("/users"), api.get("/stores")]);
      setUsers(u.data); setStores(s.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY_USER); setShowPwd(false); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, password: "", name: u.name || "", role: u.role, store_id: u.store_id || "", phone: u.phone || "" });
    setShowPwd(false);
    setOpen(true);
  };
  const resetAndClose = () => { setOpen(false); setEditing(null); setForm(EMPTY_USER); setShowPwd(false); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        // Only send changed / meaningful fields. Password is optional on edit.
        const payload = { name: form.name, phone: form.phone, role: form.role, store_id: form.role === "admin" ? null : form.store_id };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        const payload = { ...form, store_id: form.role === "admin" ? null : form.store_id };
        await api.post("/users", payload);
        toast.success("User created");
      }
      resetAndClose(); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (u) => { await api.put(`/users/${u.id}`, { active: !u.active }); reload(); };

  const askDelete = (u) => setPendingDelete(u);
  const doDelete = async () => {
    const u = pendingDelete;
    if (!u) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setPendingDelete(null); }
  };

  const storeName = (id) => stores.find(s => s.id === id)?.name || "—";

  return (
    <div className="space-y-8" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div><div className="overline">Access</div><h1 className="font-display text-4xl sm:text-5xl mt-2">Users</h1></div>
        <Dialog open={open} onOpenChange={(o) => { if (!o) resetAndClose(); else setOpen(true); }}>
          <DialogTrigger asChild><Button onClick={openNew} data-testid="add-user-btn"><Plus className="w-4 h-4 mr-2"/>New user</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">{editing ? "Edit user" : "Add user"}</DialogTitle>
              <DialogDescription className="sr-only">{editing ? "Update this account's details." : "Create a new account for the network."}</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} required data-testid="user-name"/></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})}/></div>
              </div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} required disabled={!!editing} data-testid="user-email"/></div>
              <div>
                <Label>{editing ? "New password" : "Temporary password"}{editing && <span className="text-muted-foreground font-normal ml-1">(leave blank to keep current)</span>}</Label>
                <div className="relative">
                  <Input
                    type={showPwd ? "text" : "password"}
                    value={form.password}
                    onChange={e=>setForm({...form, password:e.target.value})}
                    required={!editing}
                    minLength={editing ? 0 : 6}
                    placeholder={editing ? "••••••••" : ""}
                    className="pr-10"
                    data-testid="user-password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={()=>setShowPwd(v=>!v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                    aria-label={showPwd ? "Hide password" : "Show password"}
                    data-testid="user-password-toggle"
                    tabIndex={-1}
                  >
                    {showPwd ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                  </button>
                </div>
                {editing && <p className="text-xs text-muted-foreground mt-1">The current password is stored as a one-way hash and can't be recovered. Type a new one here to replace it.</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Role</Label>
                  <Select value={form.role} onValueChange={v=>setForm({...form, role:v})}>
                    <SelectTrigger data-testid="user-role"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="bookstore_user">Bookstore user</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.role === "bookstore_user" && (
                  <div><Label>Store</Label>
                    <Select value={form.store_id} onValueChange={v=>setForm({...form, store_id:v})}>
                      <SelectTrigger data-testid="user-store"><SelectValue placeholder="Pick store"/></SelectTrigger>
                      <SelectContent>{stores.map(s=> <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <DialogFooter><Button type="submit" data-testid="user-submit">{editing ? "Save changes" : "Create"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid-border-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-xs uppercase tracking-widest text-muted-foreground">
              <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Store</th><th className="text-right px-4 py-3">Active</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && users.length === 0 && Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk-${i}`} className="animate-pulse" data-testid="user-row-skeleton">
                  <td className="px-4 py-3"><div className="h-3 w-32 bg-muted rounded-sm"/></td>
                  <td className="px-4 py-3"><div className="h-3 w-48 bg-muted rounded-sm"/></td>
                  <td className="px-4 py-3"><div className="h-5 w-20 bg-muted rounded-full"/></td>
                  <td className="px-4 py-3"><div className="h-3 w-36 bg-muted rounded-sm"/></td>
                  <td className="px-4 py-3 text-right"><div className="h-5 w-9 bg-muted rounded-full ml-auto"/></td>
                  <td className="px-4 py-3"/>
                </tr>
              ))}
              {users.map(u => (
                <tr key={u.id} className="hover:bg-secondary/40 group">
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-3"><Badge variant={u.role==="admin"?"default":"secondary"}>{u.role}</Badge></td>
                  <td className="px-4 py-3 text-muted-foreground">{u.role==="admin" ? "—" : storeName(u.store_id)}</td>
                  <td className="px-4 py-3 text-right"><Switch checked={u.active} onCheckedChange={() => toggleActive(u)} data-testid={`user-toggle-${u.email}`}/></td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="icon" onClick={()=>openEdit(u)} data-testid={`user-edit-${u.email}`} aria-label="Edit user">
                        <Pencil className="w-4 h-4"/>
                      </Button>
                      <Button variant="ghost" size="icon" onClick={()=>askDelete(u)} data-testid={`user-delete-${u.email}`} aria-label="Delete user">
                        <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive"/>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o)=>{ if(!o) setPendingDelete(null); }}
        title={`Delete ${pendingDelete?.email}?`}
        description="This permanently removes the user login. Store data and history are untouched."
        confirmLabel="Delete user"
        destructive
        onConfirm={doDelete}
        testid="confirm-delete-user"
      />
    </div>
  );
}
